/* DRIVE IQ — ADVENTURE MODE V603.2 BULLETPROOF INVENTORY AUTHORITY
   File: adventure-provider.js

   V603.1 focused repair:
   - Normalizes Google Places rating and userRatingCount immediately at the provider boundary.
   - Preserves display name, formatted address, and exact coordinates before Scan Engine ranking.
   - Ensures Top Picks and Place IQ receive the same customer rating evidence.
   - Does not change ranking weights, category truth, provider order, pins, shelf behavior, or routing.

   V603.0 provider authority:
   - Makes Google Places (New) the primary Adventure candidate source.
   - Preserves Google Place ID as canonical identity through Scan Engine enrichment.
   - Requests Google ratings, review counts, hours, business status, website, phone,
     Maps URI, primary type, accessibility data, editorial summary, and photo references.
   - Uses Mapbox/Search Core only as a geometry and search fallback.
   - Uses Geoapify/OpenStreetMap fallback only for outdoor, trail, park, water,
     marina, boat-ramp, fishing, camping, public-land, and scenic discovery.
   - Skips secondary providers when Google returns a healthy market-aware candidate pool.
   - Deduplicates fallback candidates without replacing stronger Google records.
   - Preserves Category Truth, Event IQ, ranking, photo hydration, diagnostics,
     provider registration, refill behavior, and the existing Scan Engine contract.

   V600.24 focused repair:
   - Captures the exact Adventure search pipeline at the provider boundary.
   - Records requested candidate limit, candidates returned by Candidate Source,
     Scan Engine raw results, enriched results, photo coverage, radius, query,
     category, market class, and candidate shortfall.
   - Prints one compact console table automatically after every provider run.
   - Exposes DriveIQAdventureProviderPipeline.last(), history(), print(), and clear().
   - Does not alter provider queries, ranking, Category Truth, result volume,
     coordinates, photos, events, caching, or scan behavior.


   V600.23 focused repair:
   - Prevents a committed destination label such as Gainesville, Florida or
     Charleston, South Carolina from replacing the selected category query.
   - Distinguishes destination-only text from true typed discovery intent.
   - Keeps Food IQ, Local Favorites, Worth the Drive, and other category query
     families authoritative when the search field only contains a city.
   - Preserves explicit typed searches such as tacos in Gainesville, live music
     tonight, coffee near downtown, or family activities.
   - Emits query-authority diagnostics so future zero-result failures reveal
     whether the provider used typed intent or the category pool.

   V600.22 focused repair:
   - Gives each Adventure category its own provider query family.
   - Prevents Trending, Local Favorites, Worth the Drive, Food, Hidden Gems,
     Live Music, Nightlife, Waterfront, Outdoors, and Family from sharing
     one generic candidate request.
   - Adds discovery-round and query-variant identity to every candidate request.
   - Forces round-two and round-three continuation requests to bypass stale cache.
   - Preserves the original requested category separately from the effective
     provider category so ranking context remains accurate.
   - Adds category-specific alternate query variants for fresh continuation pulls.
   - Keeps national-chain exclusions and Scan Engine Category Truth unchanged.

   V600.21 focused repair:
   - Normalizes UI experience labels before Candidate Source and Scan Engine.
   - Treats Worth the Drive as Local Favorites discovery plus ranking context.
   - Prevents unknown UI category names from causing the Scan Engine to reject every candidate.
   - Preserves the original requested category for diagnostics and recommendation copy.
   - Leaves candidate volume, coordinates, photos, events, ratings, and provider registration intact.

   Fixes:
   - Preserves photo/media fields from raw candidates after Scan Engine ranking.
   - Reattaches the original candidate payload to each ranked result by identity.
   - Normalizes direct and nested image fields into diqPhotoUrl/photoUrl/gallery.
   - Keeps Place ID, exact coordinates, address, and provider metadata intact.
   - Emits provider:results-enriched after photo data is restored.
   - Honors canonicalQuery, resetDiscovery, forceFreshMarket, and natural intent from Scan IQ.
   - Prevents fresh Near Me searches from inheriting destination or cached market context.
   - Passes cache-busting request identity and exact center authority to candidate sources.
   - Expands provider candidate volume for category discovery before Scan Engine filtering.
   - Preserves Google rating, rating count, open status, Maps link, and review metadata.
   - Normalizes social-proof fields from direct and nested provider payloads.
   - Requests Place Details enrichment hints from compatible candidate sources.
   - Carries exact rating evidence through candidate resolution, ranking, and Place IQ.
   - Emits social-proof coverage counts for diagnostics.
   - Adds Event IQ normalization for live music, nightlife, recurring events,
     venue calendars, and current experience signals.
   - Requests compatible event-enrichment fields from candidate sources.
   - Preserves event title, type, start/end time, venue area, source, freshness,
     recurrence, and confidence through Scan Engine ranking.
   - Distinguishes Confirmed Tonight, Usually Happens Tonight, Known Music Venue,
     Upcoming Event, and No Current Event.
   - Never upgrades an unconfirmed recurring pattern into a confirmed live event.
   - Filters expired event instances while preserving known venue intelligence.
   - Emits event coverage and confidence counts for diagnostics.
   - Normalizes top-level and nested provider coordinates into one exact lat/lng authority.
   - Rejects invalid, zero-island, reversed, and implausibly distant result coordinates.
   - Prevents one malformed expanded-radius candidate from corrupting Mapbox fit bounds.
   - Emits coordinate coverage and rejection diagnostics for market-specific failures.
   - Honors Scan V600.56 market-aware provider limits without collapsing back to one global cap.
   - Requests larger raw candidate pools for tourist destinations and major metros.
   - Passes the market volume profile into Search Core and the Scan Engine.
   - Uses the full market target for ranking while preserving hard Category Truth filters.
   - Emits requested-vs-returned candidate diagnostics for sparse-market debugging.
   - Never fabricates volume when the candidate source cannot supply enough trusted places.
   - Passes every common result-limit alias to Candidate Source and Search Core.
   - Prevents dedicated candidate sources from silently falling back to an 8-result default.
   - Emits requested, returned, ranked, and committed volume diagnostics.
*/

(function(window){
"use strict";

if(window.DriveIQAdventureProvider){return;}

var registered=false;
var registering=false;
var unregisterProvider=null;
var candidateSource=null;
var lastReport=null;
var retryTimer=null;
var retryCount=0;
var MAX_RETRIES=80;
var providerPipelineHistory=[];
var providerPipelineActive={};
var PROVIDER_PIPELINE_HISTORY_LIMIT=30;

function providerPipelineKey(context){
  return clean(
    context&&(
      context.requestId||
      context.token
    )||
    "unknown"
  );
}

function providerPipelinePrint(report){
  if(!report||!window.console){return report||null;}

  try{
    console.groupCollapsed(
      "[Drive IQ Adventure Pipeline]",
      report.destination||"Unknown destination",
      "—",
      report.category||"Unknown category"
    );

    console.table({
      destination:report.destination,
      category:report.category,
      effectiveCategory:report.effectiveCategory,
      query:report.query,
      queryAuthority:report.queryAuthority,
      marketClass:report.marketClass,
      radiusMeters:report.radiusMeters,
      fallbackPass:report.fallbackPass,
      requestedCandidateLimit:report.requestedCandidateLimit,
      requestedTargetResults:report.requestedTargetResults,
      candidateSourceReturned:report.candidateSourceReturned,
      scanEngineRawResults:report.scanEngineRawResults,
      enrichedResults:report.enrichedResults,
      photoResults:report.photoResults,
      ratingResults:report.ratingResults,
      coordinateRejected:report.coordinateRejected,
      candidateShortfall:report.candidateShortfall
    });

    if(report.warning){
      console.warn(report.warning);
    }

    console.log("Full pipeline report",report);
    console.groupEnd();
  }catch(error){}

  return report;
}

function providerPipelineCommit(report){
  providerPipelineHistory.push(report);

  if(providerPipelineHistory.length>PROVIDER_PIPELINE_HISTORY_LIMIT){
    providerPipelineHistory=providerPipelineHistory.slice(
      providerPipelineHistory.length-PROVIDER_PIPELINE_HISTORY_LIMIT
    );
  }

  providerPipelinePrint(report);
  return report;
}


function config(){
  return window.DriveIQAdventureConfig||null;
}

function state(){
  return window.DriveIQAdventureState||null;
}

function events(){
  return window.DriveIQAdventureEvents||null;
}

function destinationController(){
  return window.DriveIQAdventureDestination||null;
}

function scanController(){
  return window.DriveIQAdventureScan||null;
}

function scanEngine(){
  return window.DriveIQScanEngine||null;
}

function dependenciesReady(){
  return !!(
    config()&&
    state()&&
    events()&&
    destinationController()&&
    scanController()&&
    scanEngine()&&
    typeof scanController().registerProvider==="function"&&
    typeof scanEngine().scan==="function"
  );
}

function log(){
  var cfg=config();

  if(
    !cfg||
    !cfg.logging||
    !cfg.logging.enabled||
    !window.console||
    !window.console.log
  ){
    return;
  }

  var args=Array.prototype.slice.call(arguments);
  args.unshift(cfg.logging.prefix,"[Provider]");
  window.console.log.apply(window.console,args);
}

function warn(){
  if(!window.console||!window.console.warn){return;}

  var args=Array.prototype.slice.call(arguments);
  args.unshift("[Drive IQ Adventure V600] [Provider]");
  window.console.warn.apply(window.console,args);
}

function clean(value){
  return String(value==null?"":value).replace(/\s+/g," ").trim();
}

function norm(value){
  return clean(value).toLowerCase();
}

function categoryKey(value){
  return norm(value)
    .replace(/[^a-z0-9]+/g,"_")
    .replace(/^_+|_+$/g,"");
}

/*
  V600.21 Category Intent Authority:
  Some Adventure controls are experience/ranking labels, not provider
  categories. Convert them to a proven discovery category before both
  candidate retrieval and Scan Engine ranking.
*/
function effectiveCategory(value){
  var key=categoryKey(value);
  var aliases={
    worth_the_drive:"local_favorites",
    worth_drive:"local_favorites",
    worth_it:"local_favorites",
    adventure_iq:"local_favorites",
    local_faves:"local_favorites",
    local_favorite:"local_favorites",
    food_iq:"food",
    coffee_iq:"coffee",
    water_iq:"waterfront",
    waterfront_iq:"waterfront",
    scenic:"outdoors",
    scenic_iq:"outdoors",
    family:"family_iq",
    family_fun:"family_iq",
    live_music_iq:"live_music",
    nightlife_iq:"nightlife"
  };

  return aliases[key]||key||"local_favorites";
}

function requestedCategoryKey(context){
  return categoryKey(
    context&&(
      context.requestedCategory||
      context.category||
      ""
    )
  );
}

function discoveryRound(context){
  return Math.max(
    1,
    Number(
      context&&(
        context.discoveryRound||
        context.round||
        context.refillRound||
        1
      )
    )
  );
}

function continuationMode(context){
  return categoryKey(
    context&&(
      context.refillMode||
      context.continuationMode||
      ""
    )
  );
}

function categoryQueryFamily(context){
  var requested=requestedCategoryKey(context);
  var effective=effectiveCategory(
    context&&context.category
  );
  var key=requested||effective||"local_favorites";
  var round=discoveryRound(context);
  var variant=Math.max(0,round-1);

  var families={
    food:[
      "best independent local restaurants",
      "locally owned chef driven restaurants neighborhood favorites",
      "iconic local restaurants hidden dining gems"
    ],
    food_iq:[
      "best independent local restaurants",
      "locally owned chef driven restaurants neighborhood favorites",
      "iconic local restaurants hidden dining gems"
    ],
    local_favorites:[
      "local favorites locally owned neighborhood institutions",
      "family owned local favorites beloved neighborhood places",
      "longtime local institutions and community favorites"
    ],
    local_faves:[
      "local favorites locally owned neighborhood institutions",
      "family owned local favorites beloved neighborhood places",
      "longtime local institutions and community favorites"
    ],
    worth_the_drive:[
      "destination quality local places worth the drive",
      "iconic local experiences worth traveling for",
      "signature local institutions and standout destinations"
    ],
    worth_drive:[
      "destination quality local places worth the drive",
      "iconic local experiences worth traveling for",
      "signature local institutions and standout destinations"
    ],
    trending:[
      "popular local places trending now",
      "buzzing local spots popular right now",
      "highly rated local places with strong recent interest"
    ],
    hidden_gems:[
      "hidden gems locally owned lesser known places",
      "under the radar local favorites off the beaten path",
      "small independent local gems with strong reviews"
    ],
    live_music:[
      "live music venues concerts bands tonight",
      "local music venues live performances event calendars",
      "independent live music venues bars stages and shows"
    ],
    nightlife:[
      "local nightlife cocktail lounges late night venues",
      "independent bars rooftops lounges and nightlife",
      "popular local night spots open late"
    ],
    waterfront:[
      "waterfront restaurants marinas harbor views local favorites",
      "locally owned waterfront dining and shoreline places",
      "harbor marina beach waterfront destination experiences"
    ],
    outdoors:[
      "best local parks trails scenic outdoor places",
      "nature preserves overlooks trails and local outdoor favorites",
      "lesser known scenic outdoor places and hidden nature spots"
    ],
    family_iq:[
      "best local family activities and attractions",
      "independent family fun museums arcades and attractions",
      "local family experiences kids activities and unique attractions"
    ],
    coffee:[
      "independent local coffee shops and roasters",
      "specialty coffee local roasters neighborhood cafes",
      "hidden local cafes and independent coffeehouses"
    ],
    dessert:[
      "independent local dessert shops bakeries and ice cream",
      "local bakeries gelato dessert and sweet shops",
      "hidden local dessert favorites and specialty treats"
    ]
  };

  var selected=families[key]||families[effective];

  if(!selected||!selected.length){
    return null;
  }

  return selected[Math.min(variant,selected.length-1)];
}

function categoryCacheKey(context,providerQuery){
  return [
    requestedCategoryKey(context),
    effectiveCategory(context&&context.category),
    String(context&&context.scope||""),
    String(context&&context.fallbackPass||0),
    String(discoveryRound(context)),
    String(continuationMode(context)),
    norm(providerQuery||""),
    Number(context&&context.center&&context.center.lat||0).toFixed(4),
    Number(context&&context.center&&context.center.lng||0).toFixed(4)
  ].join("|");
}

function marketVolumeProfile(context){
  var supplied=context&&context.marketVolumeProfile;

  if(supplied&&typeof supplied==="object"){
    return supplied;
  }

  return {
    marketClass:"standard",
    minimum:12,
    target:18,
    providerLimit:Math.max(
      48,
      Number(context&&context.maximumResults||0)
    ),
    rawFloor:16
  };
}

function requestedCandidateLimit(context){
  var profile=marketVolumeProfile(context);
  var supplied=Math.max(
    0,
    Number(context&&context.maximumResults||0)
  );

  return Math.max(
    24,
    supplied,
    Number(profile.providerLimit||0)
  );
}

function requestedTargetResults(context){
  var profile=marketVolumeProfile(context);

  return Math.max(
    1,
    Number(profile.target||0),
    Number(
      context&&context.targetResults||0
    )
  );
}

function candidateLimitAliases(context){
  var limit=requestedCandidateLimit(context);

  return {
    maximumResults:limit,
    maxResults:limit,
    limit:limit,
    pageSize:limit,
    candidateLimit:limit,
    resultLimit:limit
  };
}

function finiteNumber(value){
  var number=Number(value);
  return isFinite(number)?number:null;
}

function coordinatePair(latValue,lngValue){
  var lat=finiteNumber(latValue);
  var lng=finiteNumber(lngValue);

  if(
    lat===null||
    lng===null||
    Math.abs(lat)>90||
    Math.abs(lng)>180
  ){
    return null;
  }

  /* Reject accidental Zero Island coordinates. */
  if(Math.abs(lat)<0.000001&&Math.abs(lng)<0.000001){
    return null;
  }

  return {
    lat:lat,
    lng:lng
  };
}

function coordinateFromArray(value){
  var first;
  var second;
  var direct;
  var reversed;

  if(!Array.isArray(value)||value.length<2){
    return null;
  }

  first=finiteNumber(value[0]);
  second=finiteNumber(value[1]);

  if(first===null||second===null){
    return null;
  }

  /* GeoJSON and Mapbox use [lng, lat]. */
  direct=coordinatePair(second,first);
  if(direct){return direct;}

  /* Defensive fallback for provider arrays that incorrectly use [lat, lng]. */
  reversed=coordinatePair(first,second);
  return reversed;
}

function coordinateBundle(item){
  var sources=[];
  var i;
  var source;
  var pair;

  if(!item||typeof item!=="object"){
    return null;
  }

  sources.push(item);

  [
    item.location,
    item.coordinate,
    item.coordinates,
    item.center,
    item.position,
    item.geometry&&item.geometry.location,
    item.geometry,
    item.properties&&item.properties.location,
    item.properties&&item.properties.coordinates,
    item.raw&&item.raw.location,
    item.raw&&item.raw.geometry&&item.raw.geometry.location,
    item.providerData&&item.providerData.location,
    item.providerData&&item.providerData.geometry&&
      item.providerData.geometry.location,
    item.details&&item.details.location
  ].forEach(function(value){
    if(value!==undefined&&value!==null){
      sources.push(value);
    }
  });

  for(i=0;i<sources.length;i+=1){
    source=sources[i];

    if(Array.isArray(source)){
      pair=coordinateFromArray(source);
      if(pair){return pair;}
      continue;
    }

    if(!source||typeof source!=="object"){
      continue;
    }

    pair=coordinatePair(
      firstDefined(
        source.lat,
        source.latitude,
        source.y
      ),
      firstDefined(
        source.lng,
        source.lon,
        source.longitude,
        source.x
      )
    );

    if(pair){return pair;}

    if(Array.isArray(source.coordinates)){
      pair=coordinateFromArray(source.coordinates);
      if(pair){return pair;}
    }
  }

  return null;
}

function radians(value){
  return Number(value)*Math.PI/180;
}

function distanceMeters(a,b){
  var first=coordinateBundle(a);
  var second=coordinateBundle(b);
  var earthRadius=6371000;
  var dLat;
  var dLng;
  var haversine;

  if(!first||!second){return null;}

  dLat=radians(second.lat-first.lat);
  dLng=radians(second.lng-first.lng);

  haversine=
    Math.sin(dLat/2)*Math.sin(dLat/2)+
    Math.cos(radians(first.lat))*
    Math.cos(radians(second.lat))*
    Math.sin(dLng/2)*
    Math.sin(dLng/2);

  return earthRadius*2*Math.atan2(
    Math.sqrt(haversine),
    Math.sqrt(1-haversine)
  );
}

function coordinateValidation(item,context){
  var coordinate=coordinateBundle(item);
  var center=context&&coordinateBundle(context.center);
  var requestedRadius=Math.max(
    0,
    Number(context&&context.radiusMeters||0)
  );
  var fallbackPass=Math.max(
    0,
    Number(context&&context.fallbackPass||0)
  );
  var distance;
  var allowedDistance;

  if(!coordinate){
    return {
      valid:false,
      reason:"missing_or_invalid_coordinate",
      coordinate:null,
      distanceMeters:null
    };
  }

  if(!center){
    return {
      valid:true,
      reason:"no_center_for_distance_check",
      coordinate:coordinate,
      distanceMeters:null
    };
  }

  distance=distanceMeters(coordinate,center);

  /*
   * Expanded discovery may legitimately exceed the requested radius.
   * Keep a generous market guard while rejecting cross-country or malformed
   * coordinates that would destroy fitBounds.
   */
  allowedDistance=Math.max(
    requestedRadius*3,
    80000+(fallbackPass*40000)
  );

  if(distance!==null&&distance>allowedDistance){
    return {
      valid:false,
      reason:"outside_market_guard",
      coordinate:coordinate,
      distanceMeters:distance,
      allowedDistanceMeters:allowedDistance
    };
  }

  return {
    valid:true,
    reason:"valid",
    coordinate:coordinate,
    distanceMeters:distance,
    allowedDistanceMeters:allowedDistance
  };
}

function destinationTextValues(context){
  var values=[];
  var destination=context&&context.destination;
  var center=context&&context.center;

  function add(value){
    var cleaned=clean(value);

    if(!cleaned){return;}
    if(values.indexOf(norm(cleaned))<0){
      values.push(norm(cleaned));
    }
  }

  if(destination&&typeof destination==="object"){
    add(destination.label);
    add(destination.name);
    add(destination.title);
    add(destination.full);
    add(destination.address);
    add(destination.formattedAddress);
    add(destination.city);
    add(
      [
        destination.city,
        destination.state
      ].filter(Boolean).join(", ")
    );
  }else{
    add(destination);
  }

  if(center&&typeof center==="object"){
    add(center.label);
    add(center.name);
    add(center.city);
    add(
      [
        center.city,
        center.state
      ].filter(Boolean).join(", ")
    );
  }

  add(context&&context.destinationLabel);
  add(context&&context.centerLabel);
  add(context&&context.marketLabel);
  add(context&&context.locationLabel);

  return values;
}

function queryHasDiscoveryIntent(value){
  var text=norm(value);

  if(!text){return false;}

  return /(?:\bfood\b|\brestaurant|\bdining\b|\beat\b|\bbreakfast\b|\bbrunch\b|\blunch\b|\bdinner\b|\bburger|\bpizza|\btaco|\bbbq\b|\bbarbecue\b|\bseafood\b|\bsteak|\bsushi\b|\bcoffee\b|\bcafe\b|\bbakery\b|\bdessert\b|\bice cream\b|\bbrewery\b|\bbar\b|\bpub\b|\bcocktail\b|\bnightlife\b|\blive music\b|\bconcert\b|\bevent\b|\bkaraoke\b|\btrivia\b|\bopen mic\b|\bpark\b|\btrail\b|\boutdoor|\bscenic\b|\bmuseum\b|\battraction\b|\bfamily\b|\bkid|\bwaterfront\b|\bmarina\b|\bbeach\b|\bhidden gem|\blocal favorite|\bworth the drive\b|\btrending\b|\bthings to do\b|\bplaces to go\b|\bnear me\b|\btonight\b|\bopen now\b)/.test(text);
}

function queryLooksLikeDestinationOnly(value,context){
  var text=norm(value);
  var destinationValues=destinationTextValues(context);
  var compact;
  var i;

  if(!text){return false;}

  for(i=0;i<destinationValues.length;i+=1){
    if(text===destinationValues[i]){
      return true;
    }
  }

  compact=text
    .replace(/\b(united states|usa|us)\b/g," ")
    .replace(/[.,]/g," ")
    .replace(/\s+/g," ")
    .trim();

  /*
    A destination-only label is usually a short city/state string and contains
    no discovery noun. Examples: Gainesville Florida, Charleston SC, Tampa.
  */
  if(
    !queryHasDiscoveryIntent(compact)&&
    compact.split(" ").length<=5&&
    !/\b(best|top|popular|local|near|around|find|show|discover)\b/.test(compact)
  ){
    return true;
  }

  return false;
}

function shouldUseOriginalQuery(context,requestedCategory){
  var original=clean(context&&context.originalQuery);

  if(!original){return false;}
  if(norm(original)===norm(requestedCategory)){return false;}
  if(queryLooksLikeDestinationOnly(original,context)){return false;}

  return queryHasDiscoveryIntent(original);
}

function queryAuthorityForContext(context,requestedCategory){
  return shouldUseOriginalQuery(context,requestedCategory)
    ? "explicit_typed_discovery_intent"
    : "category_query_family";
}

function normalizeScope(scope){
  var value=String(scope||"near_me");

  if(value==="destination"){
    return "near_destination";
  }

  return value;
}

function scanQueryForContext(context){
  var cfg=config();
  var requestedCategory=String(
    context.requestedCategory||
    context.category||
    (cfg&&cfg.scan
      ? cfg.scan.defaultCategory
      : "local_favorites")
  );
  var category=effectiveCategory(
    context.category||requestedCategory
  );
  var familyQuery=categoryQueryFamily(
    Object.assign({},context,{
      requestedCategory:requestedCategory,
      category:category
    })
  );

  var map={
    events:"events tonight live entertainment",
    tonight:"things to do tonight live music events nightlife",
    karaoke:"karaoke tonight",
    trivia:"trivia night",
    open_mic:"open mic tonight",
    watch_party:"watch party sports bar tonight",
    lake_iq:"lake marina boat ramp fishing",
    sunset_iq:"scenic sunset waterfront",
    dog_iq:"parks outdoors",
    gameday:"nightlife sports bar"
  };

  /*
    Explicit typed discovery intent remains authoritative, but a committed
    destination label must never replace the category query family.
  */
  if(shouldUseOriginalQuery(context,requestedCategory)){
    return String(context.originalQuery);
  }

  return String(
    familyQuery||
    context.canonicalQuery||
    context.query||
    map[category]||
    category.replace(/_/g," ")
  );
}

function sourceResultArray(value){
  if(Array.isArray(value)){return value;}
  if(value&&Array.isArray(value.results)){return value.results;}
  if(value&&Array.isArray(value.candidates)){return value.candidates;}
  if(value&&Array.isArray(value.places)){return value.places;}
  return [];
}

function outdoorFallbackCategory(context){
  var key=categoryKey(
    context&&(
      context.requestedCategory||
      context.category||
      context.query||
      ""
    )
  );

  return /(?:outdoor|park|trail|hiking|nature|scenic|waterfront|water|lake|marina|boat_ramp|ramp|fishing|camp|campground|public_land|refuge|wildlife|beach|river|harbor|dock|pier)/.test(
    key
  );
}

function healthyGoogleCandidateCount(context){
  var profile=marketVolumeProfile(context);
  var target=Math.max(
    6,
    Number(profile.minimum||0),
    Math.min(
      Number(profile.target||0),
      requestedCandidateLimit(context)
    )
  );

  if(outdoorFallbackCategory(context)){
    return Math.max(8,Math.min(target,14));
  }

  return Math.max(10,Math.min(target,20));
}

function canonicalGooglePlaceId(item){
  var value=clean(item&&(
    item.googlePlaceId||
    item.google_place_id||
    item.placeId||
    item.place_id||
    item.id||
    item.name||
    ""
  ));

  if(value.indexOf("places/")===0){
    value=value.slice("places/".length);
  }

  return value;
}

function providerIdentityKey(item){
  var googleId=canonicalGooglePlaceId(item);
  var coordinate=coordinateBundle(item);
  var name=norm(item&&(item.displayName&&(
    item.displayName.text||item.displayName
  )||item.label||item.name||""));
  var address=norm(item&&(
    item.formattedAddress||
    item.address||
    item.full||
    ""
  ));

  if(googleId){return "google:"+googleId;}
  if(name&&address){return "name-address:"+name+"|"+address;}
  if(name&&coordinate){
    return "name-coordinate:"+name+"|"+
      coordinate.lat.toFixed(5)+"|"+
      coordinate.lng.toFixed(5);
  }
  if(coordinate){
    return "coordinate:"+
      coordinate.lat.toFixed(5)+"|"+
      coordinate.lng.toFixed(5);
  }

  return "anonymous:"+norm(JSON.stringify(item||{})).slice(0,180);
}

function markProviderCandidates(items,providerName){
  return sourceResultArray(items).map(function(item){
    var result=item&&typeof item==="object"
      ? Object.assign({},item)
      : item;

    if(!result||typeof result!=="object"){return result;}

    result.provider=clean(
      result.provider||
      result.sourceProvider||
      providerName
    )||providerName;

    result.sourceProvider=result.provider;

    if(providerName==="google"){
      var googleId=canonicalGooglePlaceId(result);

      if(googleId){
        result.googlePlaceId=googleId;
        result.placeId=googleId;
      }

      /*
        V603.1 Google Social Proof Boundary:
        Normalize the Google Places (New) response before Scan Engine ranking so
        customer rating evidence survives even when the ranked result no longer
        matches the original candidate by mutable name/address fields.
      */
      var googleDisplayName=clean(
        result.displayName&&(
          result.displayName.text||
          result.displayName
        )||
        result.label||
        result.name||
        ""
      );
      var googleAddress=clean(
        result.formattedAddress||
        result.shortFormattedAddress||
        result.address||
        result.full||
        ""
      );
      var googleCoordinate=coordinateBundle(result);
      var googleRating=normalizeRating(firstDefined(
        result.rating,
        result.googleRating,
        result.averageRating
      ));
      var googleRatingCount=normalizeCount(firstDefined(
        result.userRatingCount,
        result.userRatingsTotal,
        result.reviewCount,
        result.ratingCount
      ));

      if(googleDisplayName){
        result.label=result.label||googleDisplayName;
        result.name=
          typeof result.name==="string"&&clean(result.name)
            ? result.name
            : googleDisplayName;
      }

      if(googleAddress){
        result.address=result.address||googleAddress;
        result.full=result.full||googleAddress;
      }

      if(googleCoordinate){
        result.lat=googleCoordinate.lat;
        result.lng=googleCoordinate.lng;
        result.latitude=googleCoordinate.lat;
        result.longitude=googleCoordinate.lng;
      }

      if(googleRating!==null){
        result.rating=googleRating;
        result.googleRating=googleRating;
        result.averageRating=googleRating;
      }

      if(googleRatingCount!==null){
        result.userRatingCount=googleRatingCount;
        result.userRatingsTotal=googleRatingCount;
        result.reviewCount=googleRatingCount;
        result.ratingCount=googleRatingCount;
      }

      result.identityAuthority="google-place-id";
      result.inventoryAuthority="google-places-new";
    }

    return result;
  }).filter(Boolean);
}

function mergeProviderCandidates(primary,secondary){
  var output=[];
  var seen={};

  function add(item){
    var key=providerIdentityKey(item);

    if(seen[key]){return;}
    seen[key]=true;
    output.push(item);
  }

  markProviderCandidates(primary,"google").forEach(add);
  sourceResultArray(secondary).forEach(add);

  return output;
}

function googlePlacesRequest(context){
  var request=Object.assign({},context,candidateLimitAliases(context),{
    provider:"google",
    providerPreference:"google_places_new",
    inventoryAuthority:"google_places_new",
    exactCenter:true,
    includePlaceDetails:true,
    includeSocialProof:true,
    includeBusinessStatus:true,
    includeAccessibilityOptions:true,
    includeEditorialSummary:true,
    includePhotoReferences:true,
    rankPreference:outdoorFallbackCategory(context)
      ? "DISTANCE"
      : "POPULARITY",
    placeFields:[
      "id",
      "name",
      "displayName",
      "formattedAddress",
      "shortFormattedAddress",
      "location",
      "viewport",
      "types",
      "primaryType",
      "primaryTypeDisplayName",
      "businessStatus",
      "rating",
      "userRatingCount",
      "currentOpeningHours",
      "regularOpeningHours",
      "googleMapsUri",
      "websiteUri",
      "nationalPhoneNumber",
      "internationalPhoneNumber",
      "priceLevel",
      "reviews",
      "photos",
      "editorialSummary",
      "accessibilityOptions"
    ],
    fieldMask:[
      "places.id",
      "places.name",
      "places.displayName",
      "places.formattedAddress",
      "places.shortFormattedAddress",
      "places.location",
      "places.viewport",
      "places.types",
      "places.primaryType",
      "places.primaryTypeDisplayName",
      "places.businessStatus",
      "places.rating",
      "places.userRatingCount",
      "places.currentOpeningHours",
      "places.regularOpeningHours",
      "places.googleMapsUri",
      "places.websiteUri",
      "places.nationalPhoneNumber",
      "places.internationalPhoneNumber",
      "places.priceLevel",
      "places.reviews",
      "places.photos",
      "places.editorialSummary",
      "places.accessibilityOptions"
    ]
  });

  if(
    window.DriveIQGooglePlaces&&
    typeof window.DriveIQGooglePlaces.search==="function"
  ){
    return window.DriveIQGooglePlaces.search(request);
  }

  if(
    window.DriveIQGooglePlacesNew&&
    typeof window.DriveIQGooglePlacesNew.search==="function"
  ){
    return window.DriveIQGooglePlacesNew.search(request);
  }

  if(
    window.DriveIQAdventureCandidateSource&&
    typeof window.DriveIQAdventureCandidateSource.searchGoogle==="function"
  ){
    return window.DriveIQAdventureCandidateSource.searchGoogle(request);
  }

  /*
    BULLETPROOF AUTHORITY:
    Never pass a Google-shaped request into the generic Candidate Source. The
    generic source is Geoapify authority and must never be relabeled as Google.
  */
  return null;
}

function mapboxFallbackRequest(context){
  var request=Object.assign({},context,candidateLimitAliases(context),{
    provider:"mapbox",
    providerPreference:"mapbox",
    inventoryAuthority:"mapbox-fallback",
    includePlaceDetails:false,
    exactCenter:true
  });

  if(
    window.DriveIQMapboxPlaces&&
    typeof window.DriveIQMapboxPlaces.search==="function"
  ){
    return window.DriveIQMapboxPlaces.search(request);
  }

  if(
    window.DriveIQSearchCore&&
    typeof window.DriveIQSearchCore.search==="function"
  ){
    return window.DriveIQSearchCore.search(request);
  }

  return null;
}

function geoOutdoorFallbackRequest(context){
  var request=Object.assign({},context,candidateLimitAliases(context),{
    provider:"geoapify",
    providerPreference:"geoapify_osm",
    inventoryAuthority:"geoapify-outdoor-fallback",
    outdoorFallbackOnly:true,
    exactCenter:true
  });

  if(
    window.DriveIQGeoapifyPlaces&&
    typeof window.DriveIQGeoapifyPlaces.search==="function"
  ){
    return window.DriveIQGeoapifyPlaces.search(request);
  }

  if(
    window.DriveIQAdventureCandidateSource&&
    typeof window.DriveIQAdventureCandidateSource.searchGeoapify==="function"
  ){
    return window.DriveIQAdventureCandidateSource.searchGeoapify(request);
  }

  return null;
}

function geoapifyFallbackRequest(context){
  var request=Object.assign({},context,candidateLimitAliases(context),{
    provider:"geoapify",
    providerPreference:"geoapify_osm",
    inventoryAuthority:"geoapify-primary-fallback",
    exactCenter:true
  });

  if(
    window.DriveIQAdventureCandidateSource&&
    typeof window.DriveIQAdventureCandidateSource.searchGeoapify==="function"
  ){
    return window.DriveIQAdventureCandidateSource.searchGeoapify(request);
  }

  if(
    window.DriveIQAdventureCandidateSource&&
    typeof window.DriveIQAdventureCandidateSource.search==="function"
  ){
    return window.DriveIQAdventureCandidateSource.search(request);
  }

  if(
    window.DriveIQGeoapifyPlaces&&
    typeof window.DriveIQGeoapifyPlaces.search==="function"
  ){
    return window.DriveIQGeoapifyPlaces.search(request);
  }

  return null;
}

function defaultCandidateSource(context){
  var googleResponse=null;
  var googleItems=[];
  var minimum=healthyGoogleCandidateCount(context);

  try{
    googleResponse=googlePlacesRequest(context);
  }catch(googleError){
    googleResponse=null;
  }

  function geoFallback(){
    var response=null;

    try{
      response=geoapifyFallbackRequest(context);
    }catch(error){
      response=null;
    }

    if(!response){
      return Promise.resolve([]);
    }

    return Promise.resolve(response)
      .then(function(value){
        return markProviderCandidates(value,"geoapify");
      })
      .catch(function(){
        return [];
      });
  }

  function finalFallback(current){
    var retained=sourceResultArray(current);
    var fallback=null;

    if(retained.length){
      return Promise.resolve(retained);
    }

    try{
      fallback=mapboxFallbackRequest(context);
    }catch(error){
      fallback=null;
    }

    if(fallback){
      return Promise.resolve(fallback)
        .then(function(value){
          return markProviderCandidates(value,"mapbox");
        })
        .catch(function(){
          return [];
        });
    }

    if(typeof window.DRIVE_IQ_ADVENTURE_FETCH_CANDIDATES==="function"){
      try{
        return Promise.resolve(
          window.DRIVE_IQ_ADVENTURE_FETCH_CANDIDATES(context)
        ).then(sourceResultArray).catch(function(){return [];});
      }catch(error){
        return Promise.resolve([]);
      }
    }

    return Promise.resolve([]);
  }

  /*
    Google is optional enrichment/inventory authority. Geoapify is the proven
    deterministic discovery fallback. A missing or failed Google adapter can
    never collapse the scan or prevent Geoapify settlement.
  */
  return Promise.resolve(googleResponse)
    .then(function(value){
      googleItems=googleResponse
        ? markProviderCandidates(value,"google")
        : [];

      if(googleItems.length>=minimum){
        return googleItems;
      }

      return geoFallback().then(function(geoItems){
        return mergeProviderCandidates(googleItems,geoItems);
      });
    })
    .catch(function(){
      return geoFallback();
    })
    .then(finalFallback)
    .then(function(items){
      return sourceResultArray(items);
    })
    .catch(function(){
      return [];
    });
}

function resolveCandidates(context){
  var source=candidateSource||defaultCandidateSource;
  var response;

  try{
    response=source(context);
  }catch(error){
    return Promise.reject(error);
  }

  return Promise.resolve(response).then(function(value){
    if(Array.isArray(value)){return value;}
    if(value&&Array.isArray(value.results)){return value.results;}
    if(value&&Array.isArray(value.candidates)){return value.candidates;}
    return [];
  });
}

function engineOptions(context,candidates){
  var cfg=config();
  var appState=state();

  return {
    query:scanQueryForContext(context),
    intent:scanQueryForContext(context),
    category:effectiveCategory(context.category),
    requestedCategory:String(
      context.requestedCategory||
      context.category||
      ""
    ),
    discoveryRound:discoveryRound(context),
    continuationMode:continuationMode(context),
    scope:normalizeScope(context.scope),
    location:{
      lat:Number(context.center.lat),
      lng:Number(context.center.lng)
    },
    destination:context.forceFreshMarket
      ? null
      : context.destination||null,
    candidates:candidates,
    resetDiscovery:context.resetDiscovery===true,
    forceFreshMarket:context.forceFreshMarket===true,
    requestId:context.requestId||"",
    targetResults:requestedTargetResults(context),
    maximumResults:requestedCandidateLimit(context),
    marketVolumeProfile:marketVolumeProfile(context),
    userTaste:
      appState&&typeof appState.get==="function"
        ? appState.get("taste",null)
        : null,
    debug:!!(
      cfg&&
      cfg.features&&
      cfg.features.debugOverlay===true
    )||window.DRIVE_IQ_SCAN_DEBUG===true
  };
}

/* =========================================================
   PHOTO / MEDIA PRESERVATION
========================================================= */

function trustedImageURL(value){
  var url=clean(value);
  var lower=url.toLowerCase();

  if(!url){return false;}
  if(lower.indexOf("javascript:")===0){return false;}
  if(lower.indexOf("data:text")===0){return false;}

  return (
    lower.indexOf("https://")===0||
    lower.indexOf("http://")===0||
    lower.indexOf("data:image/")===0
  );
}

function pushUnique(list,value){
  if(!trustedImageURL(value)){return;}

  value=String(value);

  if(list.indexOf(value)<0){
    list.push(value);
  }
}

function collectMedia(value,list,depth){
  var keys;
  var i;
  var key;
  var child;
  var lower;

  if(value==null||depth>5){return;}

  if(typeof value==="string"){
    pushUnique(list,value);
    return;
  }

  if(Array.isArray(value)){
    for(i=0;i<value.length;i+=1){
      collectMedia(value[i],list,depth+1);
    }
    return;
  }

  if(typeof value!=="object"){return;}

  keys=Object.keys(value);

  for(i=0;i<keys.length;i+=1){
    key=keys[i];
    child=value[key];
    lower=String(key).toLowerCase();

    if(
      typeof child==="string"&&
      (
        lower.indexOf("photo")>=0||
        lower.indexOf("image")>=0||
        lower.indexOf("thumbnail")>=0||
        lower.indexOf("picture")>=0||
        lower==="url"||
        lower==="uri"||
        lower==="source"
      )
    ){
      pushUnique(list,child);
      continue;
    }

    if(
      child&&
      typeof child==="object"&&
      (
        lower.indexOf("photo")>=0||
        lower.indexOf("image")>=0||
        lower.indexOf("gallery")>=0||
        lower.indexOf("media")>=0||
        lower.indexOf("wiki")>=0||
        lower.indexOf("datasource")>=0||
        lower.indexOf("properties")>=0||
        lower.indexOf("raw")>=0||
        lower.indexOf("details")>=0||
        lower.indexOf("provider")>=0
      )
    ){
      collectMedia(child,list,depth+1);
    }
  }
}

function photoBundle(item){
  var urls=[];

  if(!item){return {primary:"",gallery:[]};}

  [
    item.diqPhotoUrl,
    item.photoURL,
    item.photoUrl,
    item.heroPhoto,
    item.heroImage,
    item.image,
    item.imageURL,
    item.imageUrl,
    item.thumbnail,
    item.thumbnailURL,
    item.thumbnailUrl,
    item.streetViewURL,
    item.streetViewUrl,
    item.mapPreviewURL,
    item.mapPreviewUrl
  ].forEach(function(value){
    pushUnique(urls,value);
  });

  collectMedia(item.photos,urls,0);
  collectMedia(item.images,urls,0);
  collectMedia(item.gallery,urls,0);
  collectMedia(item.media,urls,0);
  collectMedia(item.properties,urls,0);
  collectMedia(item.datasource,urls,0);
  collectMedia(item.raw,urls,0);
  collectMedia(item.providerData,urls,0);
  collectMedia(item.wiki_and_media,urls,0);

  return {
    primary:urls.length?urls[0]:"",
    gallery:urls
  };
}

function identityKeys(item){
  var keys=[];
  var coordinate=coordinateBundle(item);
  var lat=coordinate?coordinate.lat:null;
  var lng=coordinate?coordinate.lng:null;
  var name=norm(item&&(item.label||item.name||""));
  var address=norm(item&&(
    item.address||
    item.formattedAddress||
    item.full||
    ""
  ));
  var id=clean(item&&(
    item.placeId||
    item.place_id||
    item.googlePlaceId||
    item.google_place_id||
    item.id||
    ""
  ));

  if(id){keys.push("id:"+id);}
  if(name&&address){keys.push("na:"+name+"|"+address);}
  if(name&&lat!==null&&lng!==null){
    keys.push(
      "geo:"+
      name+"|"+
      lat.toFixed(5)+"|"+
      lng.toFixed(5)
    );
  }
  if(lat!==null&&lng!==null){
    keys.push(
      "coord:"+
      lat.toFixed(5)+"|"+
      lng.toFixed(5)
    );
  }

  return keys;
}

function candidateIndex(candidates){
  var index={};

  (Array.isArray(candidates)?candidates:[]).forEach(function(candidate){
    identityKeys(candidate).forEach(function(key){
      if(!index[key]){
        index[key]=candidate;
      }
    });
  });

  return index;
}

function findOriginalCandidate(result,index){
  var keys=identityKeys(result);
  var i;

  for(i=0;i<keys.length;i+=1){
    if(index[keys[i]]){
      return index[keys[i]];
    }
  }

  return null;
}

function firstDefined(){
  var i;

  for(i=0;i<arguments.length;i+=1){
    if(
      arguments[i]!==undefined&&
      arguments[i]!==null&&
      arguments[i]!==""
    ){
      return arguments[i];
    }
  }

  return undefined;
}

function nestedValue(source,paths){
  var i;
  var parts;
  var cursor;
  var j;

  if(!source){return undefined;}

  for(i=0;i<paths.length;i+=1){
    parts=String(paths[i]).split(".");
    cursor=source;

    for(j=0;j<parts.length;j+=1){
      if(
        cursor===undefined||
        cursor===null||
        typeof cursor!=="object"
      ){
        cursor=undefined;
        break;
      }

      cursor=cursor[parts[j]];
    }

    if(
      cursor!==undefined&&
      cursor!==null&&
      cursor!==""
    ){
      return cursor;
    }
  }

  return undefined;
}

function normalizeRating(value){
  var number=finiteNumber(value);

  if(number===null){return null;}

  if(number>5&&number<=100){
    number=number/20;
  }

  if(number<0||number>5){
    return null;
  }

  return Math.round(number*10)/10;
}

function normalizeCount(value){
  var number=finiteNumber(value);

  if(number===null||number<0){
    return null;
  }

  return Math.round(number);
}

function socialProofBundle(result,original){
  var sources=[
    result||null,
    original||null,
    result&&result.providerData,
    original&&original.providerData,
    result&&result.raw,
    original&&original.raw,
    result&&result.details,
    original&&original.details,
    result&&result.properties,
    original&&original.properties
  ];
  var rating;
  var count;
  var open;
  var mapsURL;
  var reviews;
  var i;

  for(i=0;i<sources.length;i+=1){
    if(rating==null){
      rating=normalizeRating(nestedValue(sources[i],[
        "rating",
        "googleRating",
        "averageRating",
        "userRating",
        "reviewSummary.rating",
        "place.rating",
        "details.rating"
      ]));
    }

    if(count==null){
      count=normalizeCount(nestedValue(sources[i],[
        "userRatingCount",
        "userRatingsTotal",
        "reviewCount",
        "ratingCount",
        "reviewsCount",
        "reviewSummary.count",
        "place.userRatingCount",
        "details.userRatingCount"
      ]));
    }

    if(open===undefined){
      open=nestedValue(sources[i],[
        "openNow",
        "isOpen",
        "open_now",
        "currentOpeningHours.openNow",
        "openingHours.openNow",
        "regularOpeningHours.openNow",
        "details.currentOpeningHours.openNow"
      ]);
    }

    if(!mapsURL){
      mapsURL=clean(nestedValue(sources[i],[
        "googleMapsUri",
        "googleMapsURL",
        "googleMapsUrl",
        "mapsURL",
        "mapsUrl",
        "url",
        "place.googleMapsUri",
        "details.googleMapsUri"
      ]));
    }

    if(!reviews){
      reviews=nestedValue(sources[i],[
        "reviews",
        "googleReviews",
        "reviewHighlights",
        "place.reviews",
        "details.reviews"
      ]);
    }
  }

  return {
    rating:rating,
    count:count,
    open:open,
    mapsURL:mapsURL,
    reviews:Array.isArray(reviews)?reviews.slice(0,5):[]
  };
}

function parseDateValue(value){
  var date;

  if(value===undefined||value===null||value===""){
    return null;
  }

  if(value instanceof Date){
    return isNaN(value.getTime())?null:value;
  }

  if(typeof value==="number"){
    date=new Date(
      value<100000000000
        ? value*1000
        : value
    );
    return isNaN(date.getTime())?null:date;
  }

  date=new Date(String(value));
  return isNaN(date.getTime())?null:date;
}

function normalizeEventType(value){
  var text=norm(value);

  if(/live music|band|concert|musician|performance/.test(text)){
    return "live_music";
  }
  if(/karaoke/.test(text)){return "karaoke";}
  if(/trivia/.test(text)){return "trivia";}
  if(/open mic|open-mic/.test(text)){return "open_mic";}
  if(/dj|dance party|club night/.test(text)){return "dj";}
  if(/watch party|game day|gameday/.test(text)){return "watch_party";}
  if(/happy hour/.test(text)){return "happy_hour";}
  if(/festival/.test(text)){return "festival";}
  if(/comedy/.test(text)){return "comedy";}
  if(/theater|theatre|show/.test(text)){return "show";}

  return text
    ? text.replace(/[^a-z0-9]+/g,"_").replace(/^_+|_+$/g,"")
    : "event";
}

function eventConfidenceRank(value){
  var text=norm(value);

  if(
    /confirmed tonight|confirmed_today|confirmed-today|confirmed/.test(text)
  ){
    return 5;
  }

  if(/upcoming|scheduled/.test(text)){
    return 4;
  }

  if(/usually happens tonight|recurring tonight|recurring/.test(text)){
    return 3;
  }

  if(/known music venue|known venue|frequent/.test(text)){
    return 2;
  }

  return 1;
}

function eventSourceTrust(value){
  var text=norm(value);

  if(/official website|official_website|venue website/.test(text)){return 5;}
  if(/official social|official_social|facebook|instagram/.test(text)){return 4;}
  if(/ticketmaster|bandsintown|eventbrite|event platform|event_platform/.test(text)){return 3;}
  if(/provider|google/.test(text)){return 2;}
  if(/recurring|pattern/.test(text)){return 1;}

  return 0;
}

function normalizeEventRecord(value,venue){
  var source=value&&typeof value==="object"?value:{};
  var title=clean(firstDefined(
    source.eventTitle,
    source.title,
    source.name,
    source.label,
    source.summary,
    ""
  ));
  var type=normalizeEventType(firstDefined(
    source.eventType,
    source.type,
    source.category,
    title
  ));
  var start=parseDateValue(firstDefined(
    source.startTime,
    source.start,
    source.startDateTime,
    source.dateTime,
    source.date
  ));
  var end=parseDateValue(firstDefined(
    source.endTime,
    source.end,
    source.endDateTime
  ));
  var recurring=!!firstDefined(
    source.recurring,
    source.isRecurring,
    source.recurrence,
    false
  );
  var sourceType=clean(firstDefined(
    source.sourceType,
    source.source,
    source.provider,
    recurring?"recurring_pattern":""
  ));
  var sourceURL=clean(firstDefined(
    source.sourceURL,
    source.sourceUrl,
    source.url,
    source.uri,
    ""
  ));
  var confirmedAt=parseDateValue(firstDefined(
    source.confirmedAt,
    source.updatedAt,
    source.lastVerifiedAt,
    source.fetchedAt
  ));
  var confidence=clean(firstDefined(
    source.confidence,
    source.confidenceLabel,
    ""
  ));
  var now=new Date();
  var hoursFromNow=start
    ? (start.getTime()-now.getTime())/3600000
    : null;
  var sameLocalDay=start
    ? (
        start.getFullYear()===now.getFullYear()&&
        start.getMonth()===now.getMonth()&&
        start.getDate()===now.getDate()
      )
    : false;
  var expired=!!(
    end
      ? end.getTime()<now.getTime()
      : start&&start.getTime()<now.getTime()-6*3600000
  );

  if(!confidence){
    if(
      start&&
      sameLocalDay&&
      eventSourceTrust(sourceType)>=3
    ){
      confidence="confirmed_tonight";
    }else if(start&&hoursFromNow!==null&&hoursFromNow>=0&&hoursFromNow<=168){
      confidence="upcoming_event";
    }else if(recurring){
      confidence="usually_happens_tonight";
    }else if(
      type==="live_music"||
      /music venue|live entertainment/.test(norm(
        firstDefined(
          source.venueType,
          venue&&venue.category,
          venue&&venue.categoryLabel,
          ""
        )
      ))
    ){
      confidence="known_music_venue";
    }else{
      confidence="no_current_event";
    }
  }

  return {
    eventTitle:title,
    eventType:type,
    venueName:clean(firstDefined(
      source.venueName,
      venue&&venue.label,
      venue&&venue.name,
      ""
    )),
    startTime:start?start.toISOString():"",
    endTime:end?end.toISOString():"",
    floorOrArea:clean(firstDefined(
      source.floorOrArea,
      source.area,
      source.floor,
      source.stage,
      source.room,
      ""
    )),
    sourceType:sourceType,
    sourceURL:sourceURL,
    confirmedAt:confirmedAt?confirmedAt.toISOString():"",
    recurring:recurring,
    recurrenceText:clean(firstDefined(
      source.recurrenceText,
      source.scheduleText,
      source.recurrence,
      ""
    )),
    confidence:confidence,
    confidenceRank:eventConfidenceRank(confidence),
    sourceTrust:eventSourceTrust(sourceType),
    expired:expired,
    raw:source
  };
}

function eventSourceValues(source){
  var values=[];

  if(!source||typeof source!=="object"){return values;}

  [
    source.events,
    source.currentEvents,
    source.upcomingEvents,
    source.eventCalendar,
    source.liveEvents,
    source.liveMusic,
    source.nightlifeEvents,
    source.recurringEvents,
    source.experiences
  ].forEach(function(value){
    if(Array.isArray(value)){
      values=values.concat(value);
    }else if(value&&typeof value==="object"){
      values.push(value);
    }
  });

  if(
    source.eventTitle||
    source.startTime||
    source.recurrenceText
  ){
    values.push(source);
  }

  return values;
}

function eventIQBundle(result,original){
  var sources=[
    result||null,
    original||null,
    result&&result.providerData,
    original&&original.providerData,
    result&&result.raw,
    original&&original.raw,
    result&&result.details,
    original&&original.details,
    result&&result.properties,
    original&&original.properties
  ];
  var events=[];
  var seen={};

  sources.forEach(function(source){
    eventSourceValues(source).forEach(function(value){
      var record=normalizeEventRecord(value,result||original||{});
      var key=[
        norm(record.eventTitle),
        record.startTime,
        record.eventType,
        norm(record.floorOrArea)
      ].join("|");

      if(
        record.expired||
        (!record.eventTitle&&record.confidence==="no_current_event")
      ){
        return;
      }

      if(!seen[key]){
        seen[key]=true;
        events.push(record);
      }
    });
  });

  events.sort(function(a,b){
    if(b.confidenceRank!==a.confidenceRank){
      return b.confidenceRank-a.confidenceRank;
    }
    if(b.sourceTrust!==a.sourceTrust){
      return b.sourceTrust-a.sourceTrust;
    }
    if(a.startTime&&b.startTime){
      return new Date(a.startTime)-new Date(b.startTime);
    }
    return a.startTime?-1:b.startTime?1:0;
  });

  var primary=events.length?events[0]:null;
  var text=norm([
    result&&result.category,
    result&&result.categoryLabel,
    result&&result.description,
    original&&original.category,
    original&&original.categoryLabel,
    original&&original.description
  ].join(" "));

  if(!primary&&/live music|music venue|nightlife|live entertainment/.test(text)){
    primary=normalizeEventRecord({
      eventTitle:"",
      eventType:"live_music",
      confidence:"known_music_venue",
      sourceType:"provider"
    },result||original||{});
  }

  return {
    events:events.slice(0,12),
    primary:primary,
    confirmedTonight:events.filter(function(item){
      return item.confidence==="confirmed_tonight";
    }),
    recurringTonight:events.filter(function(item){
      return item.confidence==="usually_happens_tonight";
    })
  };
}

function mergeOriginalIntoResult(result,original){
  var merged={};
  var media;

  if(original&&typeof original==="object"){
    Object.keys(original).forEach(function(key){
      merged[key]=original[key];
    });
  }

  if(result&&typeof result==="object"){
    Object.keys(result).forEach(function(key){
      merged[key]=result[key];
    });
  }

  merged.googlePlaceId=canonicalGooglePlaceId(
    result||original||{}
  );

  merged.placeId=String(firstDefined(
    merged.googlePlaceId,
    result&&result.placeId,
    result&&result.place_id,
    original&&original.placeId,
    original&&original.place_id,
    original&&original.googlePlaceId,
    ""
  )||"");

  merged.provider=clean(firstDefined(
    result&&result.provider,
    result&&result.sourceProvider,
    original&&original.provider,
    original&&original.sourceProvider,
    merged.googlePlaceId?"google":"",
    ""
  ));

  merged.inventoryAuthority=clean(firstDefined(
    result&&result.inventoryAuthority,
    original&&original.inventoryAuthority,
    merged.googlePlaceId?"google-places-new":"",
    ""
  ));

  merged.name=clean(firstDefined(
    result&&result.name,
    result&&result.label,
    original&&original.name,
    original&&original.label,
    ""
  ));

  merged.label=clean(firstDefined(
    result&&result.label,
    result&&result.name,
    original&&original.label,
    original&&original.name,
    ""
  ));

  merged.address=clean(firstDefined(
    result&&result.address,
    result&&result.formattedAddress,
    result&&result.full,
    original&&original.address,
    original&&original.formattedAddress,
    original&&original.full,
    ""
  ));

  merged.full=clean(firstDefined(
    result&&result.full,
    result&&result.address,
    original&&original.full,
    original&&original.address,
    ""
  ));

  var resultCoordinate=coordinateBundle(result);
  var originalCoordinate=coordinateBundle(original);
  var exactCoordinate=resultCoordinate||originalCoordinate;

  if(exactCoordinate){
    merged.lat=exactCoordinate.lat;
    merged.lng=exactCoordinate.lng;
    merged.latitude=exactCoordinate.lat;
    merged.longitude=exactCoordinate.lng;
    merged.location={
      latitude:exactCoordinate.lat,
      longitude:exactCoordinate.lng
    };
    merged.coordinateAuthority=resultCoordinate
      ? "ranked-result"
      : "original-candidate";
  }else{
    delete merged.lat;
    delete merged.lng;
    delete merged.latitude;
    delete merged.longitude;
    merged.coordinateAuthority="unavailable";
  }

  merged.city=clean(firstDefined(
    result&&result.city,
    original&&original.city,
    ""
  ));

  merged.state=clean(firstDefined(
    result&&result.state,
    original&&original.state,
    ""
  ));

  merged.primaryType=clean(firstDefined(
    result&&result.primaryType,
    original&&original.primaryType,
    ""
  ));
  merged.primaryTypeDisplayName=firstDefined(
    result&&result.primaryTypeDisplayName,
    original&&original.primaryTypeDisplayName,
    null
  );
  merged.types=Array.isArray(firstDefined(
    result&&result.types,
    original&&original.types
  ))
    ? firstDefined(result&&result.types,original&&original.types).slice()
    : [];
  merged.businessStatus=clean(firstDefined(
    result&&result.businessStatus,
    original&&original.businessStatus,
    ""
  ));
  merged.websiteUri=clean(firstDefined(
    result&&result.websiteUri,
    result&&result.officialWebsiteUri,
    original&&original.websiteUri,
    original&&original.officialWebsiteUri,
    ""
  ));
  merged.nationalPhoneNumber=clean(firstDefined(
    result&&result.nationalPhoneNumber,
    original&&original.nationalPhoneNumber,
    ""
  ));
  merged.internationalPhoneNumber=clean(firstDefined(
    result&&result.internationalPhoneNumber,
    original&&original.internationalPhoneNumber,
    ""
  ));
  merged.currentOpeningHours=firstDefined(
    result&&result.currentOpeningHours,
    original&&original.currentOpeningHours,
    null
  );
  merged.regularOpeningHours=firstDefined(
    result&&result.regularOpeningHours,
    original&&original.regularOpeningHours,
    null
  );
  merged.priceLevel=firstDefined(
    result&&result.priceLevel,
    original&&original.priceLevel,
    null
  );
  merged.editorialSummary=firstDefined(
    result&&result.editorialSummary,
    original&&original.editorialSummary,
    null
  );
  merged.accessibilityOptions=firstDefined(
    result&&result.accessibilityOptions,
    original&&original.accessibilityOptions,
    null
  );
  merged.googlePhotoReferences=sourceResultArray(
    firstDefined(
      result&&result.photos,
      original&&original.photos,
      []
    )
  ).map(function(photo){
    return clean(photo&&(
      photo.name||
      photo.photoReference||
      photo.photo_reference||
      ""
    ));
  }).filter(Boolean);

  merged.providerData=original||null;
  merged.originalCandidate=original||null;

  var socialProof=socialProofBundle(result,original);

  if(socialProof.rating!==null){
    merged.rating=socialProof.rating;
    merged.googleRating=socialProof.rating;
    merged.averageRating=socialProof.rating;
  }

  if(socialProof.count!==null){
    merged.userRatingCount=socialProof.count;
    merged.userRatingsTotal=socialProof.count;
    merged.reviewCount=socialProof.count;
    merged.ratingCount=socialProof.count;
  }

  if(socialProof.open!==undefined){
    merged.openNow=!!socialProof.open;
    merged.isOpen=!!socialProof.open;
    merged.open_now=!!socialProof.open;
  }

  if(socialProof.mapsURL){
    merged.googleMapsUri=socialProof.mapsURL;
    merged.googleMapsURL=socialProof.mapsURL;
  }

  if(socialProof.reviews.length){
    merged.reviews=socialProof.reviews.slice();
    merged.reviewHighlights=socialProof.reviews.slice(0,3);
  }

  merged.socialProofAuthority=
    socialProof.rating!==null||socialProof.count!==null
      ? "provider-place-details"
      : "unavailable";

  var eventIQ=eventIQBundle(result,original);

  if(eventIQ.events.length){
    merged.events=eventIQ.events.slice();
    merged.eventIQEvents=eventIQ.events.slice();
  }

  if(eventIQ.primary){
    merged.primaryEvent=eventIQ.primary;
    merged.eventTitle=eventIQ.primary.eventTitle;
    merged.eventType=eventIQ.primary.eventType;
    merged.eventStartTime=eventIQ.primary.startTime;
    merged.eventEndTime=eventIQ.primary.endTime;
    merged.eventFloorOrArea=eventIQ.primary.floorOrArea;
    merged.eventSourceType=eventIQ.primary.sourceType;
    merged.eventSourceURL=eventIQ.primary.sourceURL;
    merged.eventConfirmedAt=eventIQ.primary.confirmedAt;
    merged.eventRecurring=eventIQ.primary.recurring;
    merged.eventRecurrenceText=eventIQ.primary.recurrenceText;
    merged.eventConfidence=eventIQ.primary.confidence;
    merged.eventConfidenceRank=eventIQ.primary.confidenceRank;
  }

  merged.confirmedTonightCount=eventIQ.confirmedTonight.length;
  merged.recurringTonightCount=eventIQ.recurringTonight.length;
  merged.eventIQAuthority=
    eventIQ.primary
      ? "provider-event-iq"
      : "unavailable";

  media=photoBundle(merged);

  if(media.primary){
    merged.diqPhotoUrl=media.primary;
    merged.photoUrl=media.primary;
    merged.photoURL=media.primary;
  }

  if(media.gallery.length){
    merged.gallery=media.gallery.slice();
    merged.photos=media.gallery.slice();
  }

  merged.photoCandidateCount=media.gallery.length;
  merged.photoAuthority=
    media.primary
      ? "provider-candidate"
      : "photo-iq-resolver";

  return merged;
}

function enrichResults(results,candidates,context){
  var index=candidateIndex(candidates);
  var rejected=[];

  var enriched=(Array.isArray(results)?results:[])
    .map(function(result){
      var original=findOriginalCandidate(result,index);
      return mergeOriginalIntoResult(result,original);
    })
    .filter(function(item){
      var validation=coordinateValidation(item,context||{});

      if(!validation.valid){
        rejected.push({
          placeId:item.placeId||item.id||"",
          name:item.name||item.label||"",
          reason:validation.reason,
          distanceMeters:validation.distanceMeters,
          allowedDistanceMeters:validation.allowedDistanceMeters
        });
        return false;
      }

      item.lat=validation.coordinate.lat;
      item.lng=validation.coordinate.lng;
      item.latitude=validation.coordinate.lat;
      item.longitude=validation.coordinate.lng;
      item.distanceFromScanCenterMeters=
        validation.distanceMeters;
      item.coordinateValidated=true;

      return true;
    });

  enriched.coordinateReport={
    inputResultCount:Array.isArray(results)?results.length:0,
    validResultCount:enriched.length,
    rejectedResultCount:rejected.length,
    rejected:rejected
  };

  return enriched;
}

function normalizeEngineResult(result,candidates,context){
  var engine=scanEngine();
  var response=result||{};
  var rawResults=Array.isArray(response.results)
    ? response.results
    : [];
  var results=enrichResults(rawResults,candidates,context);

  return {
    results:results,
    preferred:results.length?results[0]:null,
    metadata:{
      provider:"drive-iq-scan-engine",
      engineBuild:
        response.build||
        (engine&&engine.build)||
        "",
      intent:response.intent||"",
      profile:response.profile||null,
      report:response.report||null,
      photoEnriched:true,
      query:response.query||"",
      candidateCount:Array.isArray(candidates)?candidates.length:0,
      coordinateInputResultCount:
        results.coordinateReport
          ? results.coordinateReport.inputResultCount
          : rawResults.length,
      coordinateValidResultCount:
        results.coordinateReport
          ? results.coordinateReport.validResultCount
          : results.length,
      coordinateRejectedResultCount:
        results.coordinateReport
          ? results.coordinateReport.rejectedResultCount
          : 0,
      coordinateRejected:
        results.coordinateReport
          ? results.coordinateReport.rejected.slice()
          : [],
      resetDiscovery:false,
      forceFreshMarket:false,
      photoResultCount:results.filter(function(item){
        return !!item.diqPhotoUrl;
      }).length,
      ratingResultCount:results.filter(function(item){
        return finiteNumber(item.rating)!==null;
      }).length,
      ratingCountResultCount:results.filter(function(item){
        return finiteNumber(
          item.userRatingCount||
          item.userRatingsTotal||
          item.reviewCount
        )!==null;
      }).length,
      mapsLinkResultCount:results.filter(function(item){
        return !!clean(
          item.googleMapsUri||
          item.googleMapsURL
        );
      }).length,
      eventResultCount:results.filter(function(item){
        return !!(
          item.primaryEvent||
          (Array.isArray(item.eventIQEvents)&&item.eventIQEvents.length)
        );
      }).length,
      confirmedTonightResultCount:results.filter(function(item){
        return Number(item.confirmedTonightCount||0)>0||
          item.eventConfidence==="confirmed_tonight";
      }).length,
      recurringTonightResultCount:results.filter(function(item){
        return Number(item.recurringTonightCount||0)>0||
          item.eventConfidence==="usually_happens_tonight";
      }).length,
      knownMusicVenueResultCount:results.filter(function(item){
        return item.eventConfidence==="known_music_venue";
      }).length
    }
  };
}

function runProvider(context){
  var engine=scanEngine();
  var eventBus=events();

  if(!engine||typeof engine.scan!=="function"){
    return Promise.reject(
      new Error("Drive IQ Scan Engine is not ready.")
    );
  }

  var requestedCategory=String(context.category||"");
  var normalizedCategory=effectiveCategory(requestedCategory);
  var providerQuery=scanQueryForContext(
    Object.assign({},context,{category:normalizedCategory})
  );

  if(normalizedCategory!==categoryKey(requestedCategory)){
    try{
      if(eventBus){
        eventBus.emit("provider:category-normalized",{
          token:context.token,
          requestedCategory:requestedCategory,
          effectiveCategory:normalizedCategory,
          query:providerQuery
        });
      }
    }catch(categoryEventError){}
  }

  var roundNumber=discoveryRound(context);
  var cacheKey=categoryCacheKey(context,providerQuery);
  var continuation=!!(
    context.isRefill===true||
    context.calmContinuation===true||
    roundNumber>1||
    continuationMode(context)
  );

  var candidateContext={
    token:context.token,
    requestId:[
      String(context.token||""),
      requestedCategoryKey(context),
      normalizedCategory,
      String(context.fallbackPass||0),
      String(roundNumber),
      continuationMode(context),
      String(Date.now())
    ].join(":"),
    cacheKey:cacheKey,
    categoryCacheKey:cacheKey,
    discoveryRound:roundNumber,
    continuationRound:roundNumber,
    continuationMode:continuationMode(context),
    isRefill:context.isRefill===true,
    query:providerQuery,
    queryAuthority:queryAuthorityForContext(context,requestedCategory),
    destinationOnlyOriginalQuery:queryLooksLikeDestinationOnly(
      context.originalQuery,
      context
    ),
    canonicalQuery:String(
      context.canonicalQuery||
      providerQuery||
      ""
    ),
    originalQuery:String(
      context.originalQuery||
      context.query||
      ""
    ),
    category:normalizedCategory,
    requestedCategory:requestedCategory,
    scope:normalizeScope(context.scope),
    center:context.center,
    location:context.center,
    radiusMeters:context.radiusMeters,
    fallbackPass:context.fallbackPass,
    destination:context.forceFreshMarket
      ? null
      : context.destination,
    lastFix:context.lastFix,
    maximumResults:requestedCandidateLimit(context),
    maxResults:requestedCandidateLimit(context),
    limit:requestedCandidateLimit(context),
    pageSize:requestedCandidateLimit(context),
    candidateLimit:requestedCandidateLimit(context),
    resultLimit:requestedCandidateLimit(context),
    targetResults:requestedTargetResults(context),
    marketVolumeProfile:marketVolumeProfile(context),
    resetDiscovery:context.resetDiscovery===true,
    forceFreshMarket:context.forceFreshMarket===true,
    bypassCache:(
      context.forceFreshMarket===true||
      continuation
    ),
    noCache:continuation,
    freshRequest:continuation,
    cacheMode:continuation?"reload":"default",
    exactCenter:true,
    includePlaceDetails:true,
    includeSocialProof:true,
    includeEventIQ:true,
    includeLiveMusic:true,
    includeRecurringEvents:true,
    eventWindowHours:36,
    eventSourceTypes:[
      "official_website",
      "official_social",
      "event_platform",
      "provider",
      "recurring_pattern"
    ],
    placeFields:[
      "id",
      "displayName",
      "formattedAddress",
      "location",
      "rating",
      "userRatingCount",
      "currentOpeningHours",
      "googleMapsUri",
      "reviews",
      "photos"
    ],
    fieldMask:[
      "places.id",
      "places.displayName",
      "places.formattedAddress",
      "places.location",
      "places.rating",
      "places.userRatingCount",
      "places.currentOpeningHours",
      "places.googleMapsUri",
      "places.reviews",
      "places.photos"
    ],
    intent:context.intent||null,
    isCurrent:context.isCurrent,
    progress:context.progress
  };

  var pipelineKey=providerPipelineKey(candidateContext);
  providerPipelineActive[pipelineKey]={
    startedAt:new Date().toISOString(),
    token:String(context.token||""),
    requestId:String(candidateContext.requestId||""),
    destination:clean(
      context.destination&&(
        context.destination.label||
        context.destination.name||
        context.destination.full
      )||
      context.center&&context.center.label||
      ""
    ),
    category:requestedCategory,
    effectiveCategory:normalizedCategory,
    query:candidateContext.query,
    queryAuthority:candidateContext.queryAuthority,
    marketClass:clean(
      candidateContext.marketVolumeProfile&&
      candidateContext.marketVolumeProfile.marketClass||
      "unknown"
    ),
    radiusMeters:Number(candidateContext.radiusMeters||0),
    fallbackPass:Number(candidateContext.fallbackPass||0),
    requestedCandidateLimit:Number(
      candidateContext.maximumResults||0
    ),
    requestedTargetResults:Number(
      candidateContext.targetResults||0
    ),
    candidateSourceReturned:0,
    scanEngineRawResults:0,
    enrichedResults:0,
    photoResults:0,
    ratingResults:0,
    coordinateRejected:0,
    candidateShortfall:0,
    warning:""
  };

  context.progress(
    "discovering",
    /live_music|events|tonight|karaoke|trivia|open_mic|watch_party/.test(
      String(context.category||"")
    )
      ? "Finding live experiences and real venues"
      : "Finding real places",
    0.22
  );

  return resolveCandidates(candidateContext)
    .then(function(candidates){
      var result;
      var normalized;
      var pipelineReport=
        providerPipelineActive[pipelineKey]||
        {};

      pipelineReport.candidateSourceReturned=
        Array.isArray(candidates)?candidates.length:0;
      pipelineReport.candidateShortfall=Math.max(
        0,
        Number(candidateContext.targetResults||0)-
        pipelineReport.candidateSourceReturned
      );

      if(
        pipelineReport.candidateSourceReturned<
        Math.min(
          Number(candidateContext.targetResults||0),
          8
        )
      ){
        pipelineReport.warning=
          "Candidate Source returned a sparse pool before Scan Engine filtering.";
      }

      if(!context.isCurrent()){
        return {
          results:[],
          preferred:null,
          metadata:{cancelled:true}
        };
      }

      if(
        candidates.length===8&&
        Number(candidateContext.maximumResults||0)>8
      ){
        warn(
          "Candidate source returned exactly 8 despite larger market request",
          {
            requested:candidateContext.maximumResults,
            returned:candidates.length,
            marketClass:
              candidateContext.marketVolumeProfile&&
              candidateContext.marketVolumeProfile.marketClass,
            query:candidateContext.query,
            category:candidateContext.category,
            radiusMeters:candidateContext.radiusMeters
          }
        );
      }

      context.progress(
        "ranking",
        "Applying Category Truth and Worth the Drive",
        0.70
      );

      if(eventBus){
        eventBus.emit("provider:candidates-resolved",{
          token:context.token,
          requestId:candidateContext.requestId,
          query:candidateContext.query,
          queryAuthority:candidateContext.queryAuthority,
          originalQuery:candidateContext.originalQuery,
          destinationOnlyOriginalQuery:
            candidateContext.destinationOnlyOriginalQuery,
          canonicalQuery:candidateContext.canonicalQuery,
          category:candidateContext.category,
          requestedCategory:requestedCategory,
          effectiveCategory:normalizedCategory,
          scope:candidateContext.scope,
          center:candidateContext.center,
          radiusMeters:candidateContext.radiusMeters,
          fallbackPass:candidateContext.fallbackPass,
          forceFreshMarket:candidateContext.forceFreshMarket,
          resetDiscovery:candidateContext.resetDiscovery,
          discoveryRound:candidateContext.discoveryRound,
          continuationMode:candidateContext.continuationMode,
          cacheKey:candidateContext.cacheKey,
          bypassCache:candidateContext.bypassCache,
          categoryPoolSeparated:true,
          requestedCandidateLimit:
            candidateContext.maximumResults,
          requestedTargetResults:
            candidateContext.targetResults,
          marketVolumeProfile:
            candidateContext.marketVolumeProfile,
          candidateCount:candidates.length,
          requestedMaximumResults:
            candidateContext.maximumResults,
          requestedMaxResults:
            candidateContext.maxResults,
          requestedLimit:
            candidateContext.limit,
          requestedPageSize:
            candidateContext.pageSize,
          requestedCandidateLimitAlias:
            candidateContext.candidateLimit,
          sourceReturnedOnlyEight:
            candidates.length===8&&
            Number(candidateContext.maximumResults||0)>8,
          candidateShortfall:Math.max(
            0,
            Number(candidateContext.targetResults||0)-
            candidates.length
          )
        });
      }

      result=engine.scan(
        engineOptions(
          Object.assign({},context,{
            category:normalizedCategory,
            requestedCategory:requestedCategory
          }),
          candidates
        )
      );

      lastReport=
        result&&result.report
          ? result.report
          : null;

      normalized=normalizeEngineResult(
        result,
        candidates,
        context
      );

      pipelineReport.scanEngineRawResults=
        result&&Array.isArray(result.results)
          ? result.results.length
          : 0;
      pipelineReport.enrichedResults=
        normalized&&Array.isArray(normalized.results)
          ? normalized.results.length
          : 0;
      pipelineReport.photoResults=Number(
        normalized&&normalized.metadata&&
        normalized.metadata.photoResultCount||0
      );
      pipelineReport.ratingResults=Number(
        normalized&&normalized.metadata&&
        normalized.metadata.ratingResultCount||0
      );
      pipelineReport.coordinateRejected=Number(
        normalized&&normalized.metadata&&
        normalized.metadata.coordinateRejectedResultCount||0
      );
      pipelineReport.completedAt=new Date().toISOString();

      if(
        !pipelineReport.warning&&
        pipelineReport.candidateSourceReturned>=8&&
        pipelineReport.enrichedResults<=3
      ){
        pipelineReport.warning=
          "Candidate volume is healthy, but Scan Engine or enrichment reduced the result set sharply.";
      }

      providerPipelineCommit(pipelineReport);
      delete providerPipelineActive[pipelineKey];

      normalized.metadata.query=candidateContext.query;
      normalized.metadata.queryAuthority=
        candidateContext.queryAuthority;
      normalized.metadata.originalQuery=
        candidateContext.originalQuery;
      normalized.metadata.destinationOnlyOriginalQuery=
        candidateContext.destinationOnlyOriginalQuery;
      normalized.metadata.requestedCategory=requestedCategory;
      normalized.metadata.effectiveCategory=normalizedCategory;
      normalized.metadata.canonicalQuery=
        candidateContext.canonicalQuery;
      normalized.metadata.requestId=
        candidateContext.requestId;
      normalized.metadata.resetDiscovery=
        candidateContext.resetDiscovery;
      normalized.metadata.forceFreshMarket=
        candidateContext.forceFreshMarket;
      normalized.metadata.radiusMeters=
        candidateContext.radiusMeters;
      normalized.metadata.fallbackPass=
        candidateContext.fallbackPass;
      normalized.metadata.discoveryRound=
        candidateContext.discoveryRound;
      normalized.metadata.continuationMode=
        candidateContext.continuationMode;
      normalized.metadata.cacheKey=
        candidateContext.cacheKey;
      normalized.metadata.bypassCache=
        candidateContext.bypassCache;
      normalized.metadata.categoryPoolSeparated=true;
      normalized.metadata.requestedCandidateLimit=
        candidateContext.maximumResults;
      normalized.metadata.requestedMaxResults=
        candidateContext.maxResults;
      normalized.metadata.requestedLimit=
        candidateContext.limit;
      normalized.metadata.requestedPageSize=
        candidateContext.pageSize;
      normalized.metadata.requestedCandidateLimitAlias=
        candidateContext.candidateLimit;
      normalized.metadata.sourceReturnedOnlyEight=
        candidates.length===8&&
        Number(candidateContext.maximumResults||0)>8;
      normalized.metadata.requestedTargetResults=
        candidateContext.targetResults;
      normalized.metadata.marketVolumeProfile=
        candidateContext.marketVolumeProfile;
      normalized.metadata.candidateShortfall=Math.max(
        0,
        Number(candidateContext.targetResults||0)-
        candidates.length
      );

      if(eventBus){
        eventBus.emit("provider:scan-report",{
          token:context.token,
          report:lastReport,
          build:
            result&&result.build
              ? result.build
              : engine.build
        });

        eventBus.emit("provider:results-enriched",{
          token:context.token,
          candidateCount:candidates.length,
          query:candidateContext.query,
          queryAuthority:candidateContext.queryAuthority,
          originalQuery:candidateContext.originalQuery,
          destinationOnlyOriginalQuery:
            candidateContext.destinationOnlyOriginalQuery,
          requestedCandidateLimit:
            candidateContext.maximumResults,
          requestedTargetResults:
            candidateContext.targetResults,
          marketVolumeProfile:
            candidateContext.marketVolumeProfile,
          candidateShortfall:
            normalized.metadata.candidateShortfall,
          resultCount:normalized.results.length,
          requestedMaximumResults:
            candidateContext.maximumResults,
          requestedMaxResults:
            candidateContext.maxResults,
          requestedLimit:
            candidateContext.limit,
          requestedPageSize:
            candidateContext.pageSize,
          requestedCandidateLimitAlias:
            candidateContext.candidateLimit,
          sourceReturnedOnlyEight:
            normalized.metadata.sourceReturnedOnlyEight,
          photoResultCount:
            normalized.metadata.photoResultCount,
          ratingResultCount:
            normalized.metadata.ratingResultCount,
          ratingCountResultCount:
            normalized.metadata.ratingCountResultCount,
          mapsLinkResultCount:
            normalized.metadata.mapsLinkResultCount,
          eventResultCount:
            normalized.metadata.eventResultCount,
          confirmedTonightResultCount:
            normalized.metadata.confirmedTonightResultCount,
          recurringTonightResultCount:
            normalized.metadata.recurringTonightResultCount,
          knownMusicVenueResultCount:
            normalized.metadata.knownMusicVenueResultCount,
          coordinateInputResultCount:
            normalized.metadata.coordinateInputResultCount,
          coordinateValidResultCount:
            normalized.metadata.coordinateValidResultCount,
          coordinateRejectedResultCount:
            normalized.metadata.coordinateRejectedResultCount,
          coordinateRejected:
            normalized.metadata.coordinateRejected,
          results:normalized.results
        });
      }

      return normalized;
    })
    .catch(function(error){
      warn("Provider failed",error);

      if(eventBus){
        eventBus.emit("provider:failed",{
          token:context.token,
          message:String(
            error&&error.message
              ? error.message
              : error||"Provider failed"
          )
        });
      }

      throw error;
    });
}

function scheduleRegistrationRetry(){
  if(registered||retryTimer||retryCount>=MAX_RETRIES){
    return;
  }

  retryTimer=window.setTimeout(function(){
    retryTimer=null;
    retryCount+=1;
    register();

    if(!registered){
      scheduleRegistrationRetry();
    }
  },100);
}

function register(){
  var controller;
  var engine;
  var eventBus;

  if(registered){return true;}
  if(registering){return false;}

  if(!dependenciesReady()){
    scheduleRegistrationRetry();
    return false;
  }

  registering=true;
  controller=scanController();
  engine=scanEngine();
  eventBus=events();

  try{
    unregisterProvider=controller.registerProvider(
      "google-places-primary",
      runProvider,
      {
        priority:100,
        enabled:true
      }
    );

    registered=true;
    registering=false;

    if(eventBus){
      eventBus.emit("provider:registered",{
        name:"google-places-primary",
        build:engine.build||""
      });
    }

    log(
      "Registered",
      engine.build||"unknown build"
    );

    return true;
  }catch(error){
    registering=false;
    warn("Registration failed",error);
    scheduleRegistrationRetry();
    return false;
  }
}

function unregister(){
  var eventBus=events();

  if(retryTimer){
    window.clearTimeout(retryTimer);
    retryTimer=null;
  }

  if(unregisterProvider){
    unregisterProvider();
    unregisterProvider=null;
  }

  registered=false;
  registering=false;

  if(eventBus){
    eventBus.emit("provider:unregistered",{
      name:"google-places-primary"
    });
  }

  return true;
}

function registerCandidateSource(fn){
  var eventBus=events();

  if(fn!==null&&typeof fn!=="function"){
    throw new Error(
      "Adventure candidate source must be a function or null."
    );
  }

  candidateSource=fn;

  if(eventBus){
    eventBus.emit(
      "provider:candidate-source-changed",
      {
        custom:typeof fn==="function"
      }
    );
  }

  return function removeCandidateSource(){
    if(candidateSource===fn){
      candidateSource=null;
    }
  };
}

function scan(options){
  var controller;

  register();
  controller=scanController();

  if(
    !controller||
    typeof controller.start!=="function"
  ){
    return false;
  }

  return controller.start(options||{});
}

function status(){
  var engine=scanEngine();

  return {
    build:"V603_2_BULLETPROOF_INVENTORY_AUTHORITY",
    registered:registered,
    registering:registering,
    dependenciesReady:dependenciesReady(),
    retryCount:retryCount,
    scanEngineBuild:
      engine&&engine.build
        ? engine.build
        : "",
    hasCustomCandidateSource:
      typeof candidateSource==="function",
    hasCandidateSource:!!(
      window.DriveIQAdventureCandidateSource&&
      typeof window.DriveIQAdventureCandidateSource.search==="function"
    ),
    hasSearchCore:!!(
      window.DriveIQSearchCore&&
      typeof window.DriveIQSearchCore.search==="function"
    ),
    hasLegacyCandidateHook:
      typeof window.DRIVE_IQ_ADVENTURE_FETCH_CANDIDATES==="function",
    categoryPoolSeparated:true,
    supportsDiscoveryRounds:true,
    supportsCategoryCacheKeys:true,
    lastReport:lastReport
  };
}

window.DriveIQAdventureProviderPipeline={
  build:"V603.2_BULLETPROOF_INVENTORY_AUTHORITY",
  last:function(){
    return providerPipelineHistory.length
      ? providerPipelineHistory[
          providerPipelineHistory.length-1
        ]
      : null;
  },
  history:function(){
    return providerPipelineHistory.slice();
  },
  active:function(){
    return Object.assign({},providerPipelineActive);
  },
  print:function(){
    return providerPipelinePrint(
      providerPipelineHistory.length
        ? providerPipelineHistory[
            providerPipelineHistory.length-1
          ]
        : null
    );
  },
  clear:function(){
    providerPipelineHistory=[];
    providerPipelineActive={};
    return true;
  }
};

window.DriveIQAdventureProvider={
  register:register,
  unregister:unregister,
  registerCandidateSource:registerCandidateSource,
  scan:scan,
  status:status,
  runProvider:runProvider,
  enrichResults:enrichResults,
  photoBundle:photoBundle,
  socialProofBundle:socialProofBundle,
  eventIQBundle:eventIQBundle,
  normalizeEventRecord:normalizeEventRecord,
  coordinateBundle:coordinateBundle,
  coordinateValidation:coordinateValidation,
  distanceMeters:distanceMeters,
  marketVolumeProfile:marketVolumeProfile,
  requestedCandidateLimit:requestedCandidateLimit,
  requestedTargetResults:requestedTargetResults,
  candidateLimitAliases:candidateLimitAliases,
  categoryQueryFamily:categoryQueryFamily,
  categoryCacheKey:categoryCacheKey,
  discoveryRound:discoveryRound,
  destinationTextValues:destinationTextValues,
  queryHasDiscoveryIntent:queryHasDiscoveryIntent,
  queryLooksLikeDestinationOnly:queryLooksLikeDestinationOnly,
  shouldUseOriginalQuery:shouldUseOriginalQuery,
  queryAuthorityForContext:queryAuthorityForContext,
  googlePlacesRequest:googlePlacesRequest,
  mapboxFallbackRequest:mapboxFallbackRequest,
  geoOutdoorFallbackRequest:geoOutdoorFallbackRequest,
  outdoorFallbackCategory:outdoorFallbackCategory,
  healthyGoogleCandidateCount:healthyGoogleCandidateCount,
  canonicalGooglePlaceId:canonicalGooglePlaceId,
  mergeProviderCandidates:mergeProviderCandidates
};

register();

if(document.readyState==="loading"){
  document.addEventListener(
    "DOMContentLoaded",
    register,
    {once:true}
  );
}

window.addEventListener(
  "load",
  register,
  {once:true}
);

})(window);
